import { useEffect, useRef, useState } from 'react';

/**
 * Reveals streamed text gradually instead of in network-sized jumps. Catches up
 * faster the larger the backlog is; shows the full text immediately when inactive.
 */
export function useSmoothText(target: string, active: boolean): string {
  const [shown, setShown] = useState(active ? 0 : target.length);
  const targetRef = useRef(target);
  targetRef.current = target;

  useEffect(() => {
    if (!active) { setShown(targetRef.current.length); return; }
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(target.length);
      return;
    }
    let frame = 0;
    const tick = () => {
      setShown((current) => {
        const length = targetRef.current.length;
        if (current >= length) return length;
        return Math.min(length, current + Math.max(1, Math.ceil((length - current) / 8)));
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return active ? target.slice(0, shown) : target;
}
